'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.0.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init Bartender after mobile menu is ready
		document.addEventListener('mobilemenu-init-done', () => {
			window.bartender = new Bartender({
				debug: false,
				trapFocus: true,
				overlay: true,
			});
		});

		// Init mobile menu
		const mobileMenu = new MobileMenu();
		mobileMenu.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initNavHighlight();
		this.initOffCanvasRightLinks();
		this.initOffCanvasLeftLinks();
		this.initPDFDownload();
		this.initAccordions();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 *
	 * Find all links to images and use GLightbox to display them.
	 */
	initImageLinks() {
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);

		if (!parentNode) return;

		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			let figcaption = link.parentNode.querySelector('figcaption');
			let caption = figcaption ? figcaption.textContent : '';

			link.addEventListener('click', e => {
				e.preventDefault();

				const lightbox = GLightbox();
				lightbox.setElements([
					{
						href: link.href,
						type: 'image',
						title: caption
					}
				]);

				lightbox.open();
			});
		});
	}

	initNavHighlight () {
		'use strict';
		var SectionScroller = {
			anchorTops: [],

			el: {
				anchors: document.querySelectorAll('.anchor'),
				anchorLinks: document.querySelectorAll('.scroll-nav__link')
			},

			forEach: function(array, callback, scope) {
				for (var i = 0, ii = array.length; i < ii; i++) {
					callback.call(scope, i, array[i]);
				}
			},

			throttle: function (fn, threshhold, scope) {
				threshhold = threshhold || (threshhold = 250);
				var last;
				var deferTimer;
				return function () {
					var context = scope || this;
					var now = +new Date();
					var args = arguments;
					if (last && now < last + threshhold) {
						// hold on to it
						clearTimeout(deferTimer);
						deferTimer = setTimeout(function () {
							last = now;
							fn.apply(context, args);
						}, threshhold);
					} else {
						last = now;
						fn.apply(context, args);
					}
				};
			},

			mathSign: function (x) {
				x = +x; // convert to a number
				if (x === 0 || isNaN(x)) {
					return x;
				}
				return x > 0 ? 1 : -1;
			},

			anchorGetter: function () {
				var SS = SectionScroller;
				for (var i = 0, max = SS.el.anchors.length; i < max; i++) {
					SS.anchorTops[i] = SS.el.anchors[i].offsetTop;
				}
				for (var j = 0, jj = SS.anchorTops.length; j < jj; j++) {
					if (SS.anchorTops[j] - 1 < window.scrollY + (window.innerHeight * .25)) {
						for (var x = 0, xx = SS.el.anchors.length; x < xx; x++) {
							SS.el.anchorLinks[x].classList.remove('scroll-nav__link--selected');
						}
						SS.el.anchorLinks[j].classList.add('scroll-nav__link--selected');
					}
				}
			},

			events: function () {
				var SS = SectionScroller;
				window.addEventListener('scroll', SS.throttle(SS.anchorGetter, 150));
				SS.forEach(SS.el.anchorLinks, function (index, link) {
					link.addEventListener('click', SS.smooth);
				});
			},

			init: function () {
				SectionScroller.anchorGetter();
				SectionScroller.events();
			}
		};

		SectionScroller.init();
	}

	initOffCanvasRightLinks () {
		const links = document.querySelectorAll('.offcanvas-right-link')

		links.forEach(link => {
			link.addEventListener('click', e => {
				e.preventDefault()

				const appendTo = document.getElementById('bartender-bar-content');
				const url = e.target.closest('a').getAttribute('href')

				appendTo.innerHTML = ''
				window.bartender.open('right')

				fetch(url, {
					headers: { 'X-Requested-With': 'XMLHttpRequest' }
				})
					.then(response => response.text())
					.then(data => {
						appendTo.innerHTML = data;
					})
					.catch(error => {
						console.log(error);
					})
					.finally(() => {
						// buttonWrap.classList.remove('showMore--loading')
						// button.removeAttribute('disabled')
					})
			})
		})
	}

	/**
	* Initialize accordions
	*/
	initAccordions() {

		// Find accordion headers
		const accordionHeaders = document.querySelectorAll('[data-accordion-header]');
		if (!accordionHeaders.length) return;

		// Add expand event to accordion buttons
		Array.prototype.forEach.call(accordionHeaders, accordionHeader => {
			let target = document.getElementById(accordionHeader.getAttribute('aria-controls'));
			if (target) {
				accordionHeader.onclick = () => {
					let expanded = accordionHeader.getAttribute('aria-expanded') == 'true' || false;
					accordionHeader.setAttribute('aria-expanded', !expanded);
					target.hidden = expanded;
				}
			}
		});
	}

	/**
	 * Initialize PDF download button with polling for background generation
	 */
	initPDFDownload() {
		const pdfButtons = document.querySelectorAll('.js-pdf-button');
		if (!pdfButtons.length) return;

		pdfButtons.forEach(button => {
			const textEl = button.querySelector('.js-pdf-button-text');
			const iconEl = button.querySelector('.js-pdf-button-icon');
			let polling = false;
			let pollCount = 0;
			const maxPolls = 60;

			const setLoading = (text) => {
				iconEl.classList.remove('icon-download');
				iconEl.classList.add('icon-spinner', 'animate-spin');
				textEl.textContent = text;
				button.classList.add('button--disabled', 'is-loading');
			};

			const reset = (text) => {
				polling = false;
				iconEl.classList.remove('icon-spinner', 'animate-spin');
				iconEl.classList.add('icon-download');
				textEl.textContent = text || button.dataset.textDefault;
				button.classList.remove('button--disabled', 'is-loading');
			};

			const poll = () => {
				if (document.hidden) {
					setTimeout(poll, 5000);
					return;
				}

				pollCount++;
				if (pollCount > maxPolls) {
					reset(button.dataset.textError);
					return;
				}

				fetch(button.dataset.statusUrl)
					.then(r => r.json())
					.then(data => {
						if (data.status === 'ready') {
							textEl.textContent = button.dataset.textDownloading;
							window.location.href = button.dataset.pdfUrl;
							setTimeout(() => reset(), 2000);
						} else if (data.status === 'not_started' && pollCount > 1) {
							reset(button.dataset.textError);
						} else {
							setTimeout(poll, 5000);
						}
					})
					.catch(() => setTimeout(poll, 5000));
			};

			const startGeneration = () => {
				polling = true;
				pollCount = 0;
				setLoading(button.dataset.textGenerating);
				fetch(button.dataset.generateUrl).catch(() => {});
				setTimeout(poll, 5000);
			};

			button.addEventListener('click', (e) => {
				e.preventDefault();
				if (polling || button.classList.contains('button--disabled')) return;

				setLoading(button.dataset.textDownloading);

				fetch(button.dataset.statusUrl)
					.then(r => r.json())
					.then(data => {
						if (data.status === 'ready') {
							window.location.href = button.dataset.pdfUrl;
							setTimeout(() => reset(), 2000);
						} else {
							startGeneration();
						}
					})
					.catch(() => {
						window.location.href = button.dataset.pdfUrl;
						setTimeout(() => reset(), 2000);
					});
			});
		});
	}

	/**
	* Initialize off-canvas links
	*
	* Find all links and close off-canvas when clicking them
	*/
	initOffCanvasLeftLinks() {
		let navLinks = document.querySelectorAll('[data-bartender-bar="left"] .scroll-nav__link')

		navLinks.forEach(item => {
			item.addEventListener('click', () => {
				if (!window.bartender) return

				window.bartender.close()

			})
		})
	}
}

window._site = new Site();
